import React, { useEffect } from 'react';
import { Modal, Form, Input, InputNumber, DatePicker, Select, Row, Col, message } from 'antd';
import dayjs from 'dayjs';
import { apiRequest } from '../../../service/apiRequest';

const { Option } = Select;
const { TextArea } = Input;

interface ExpenseModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  isEditMode: boolean;
  initialValues: any;
}

const ExpenseModal: React.FC<ExpenseModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  isEditMode,
  initialValues,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible && isEditMode && initialValues) {
      form.setFieldsValue({
        ...initialValues,
        expense_date: initialValues.expense_date ? dayjs(initialValues.expense_date) : null,
      });
    } else {
      form.resetFields();
    }
  }, [visible, isEditMode, initialValues, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        expense_id: isEditMode ? initialValues.expense_id : undefined,
        expense_date: values.expense_date.format('YYYY-MM-DD'),
      };

      const response = await apiRequest({
        method: 'post', // The controller uses 'post' for both insert and update (upsertExpense)
        url: '/upsert-expense',
        data: payload,
      });

      if (response) {
        message.success(isEditMode ? 'Expense updated' : 'Expense added');
        onSuccess();
        onCancel();
      }
    } catch (error) {
      console.error('Submit Expense Error:', error);
    }
  };

  return (
    <Modal
      title={isEditMode ? 'Edit Expense' : 'Add New Expense'}
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      width={600}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="expense_title"
              label="Expense Title"
              rules={[{ required: true, message: 'Please enter title' }]}
            >
              <Input placeholder="e.g. Office Rent, Electricity Bill" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="expense_amount"
              label="Amount (₹)"
              rules={[{ required: true, message: 'Please enter amount' }]}
            >
              <InputNumber style={{ width: '100%' }} min={0} placeholder="0.00" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="expense_date"
              label="Expense Date"
              rules={[{ required: true, message: 'Please select date' }]}
            >
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="expense_category_id"
              label="Category"
              rules={[{ required: true, message: 'Please select category' }]}
            >
              <Select placeholder="Select Category">
                <Option value={1}>Travel</Option>
                <Option value={2}>Food</Option>
                <Option value={3}>Office</Option>
                <Option value={4}>Misc</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="expense_payment_mode"
              label="Payment Method"
              rules={[{ required: true, message: 'Please select method' }]}
            >
              <Select placeholder="Select Method">
                <Option value="Cash">Cash</Option>
                <Option value="Bank">Bank</Option>
                <Option value="UPI">UPI</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name="expense_remarks" label="Notes">
              <TextArea rows={3} placeholder="Additional details..." />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ExpenseModal;
